import { useEffect, useRef, useState } from "react";
import { Loader2, MessagesSquare } from "@digital-twins/design-system/icons";
import type { PreparedConversation } from "@digital-twins/shared-types/assistant";

import { TrialLimitUpgradeModal } from "@/components/billing/TrialLimitUpgradeModal";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import { defaultContentLanguageFromUi } from "@/i18n";
import { getThreadPlaceholders, resolveThreadLanguage } from "@/lib/constants/languages";
import { populationsApi, type AudienceSummary } from "@/lib/api";
import type { AssistantLinkedRoom } from "@/lib/api/client";
import { isMeteringEnrolled } from "@/lib/billing/metering";
import { useCreditInfo } from "@/lib/hooks";

import { formatActivityPlural } from "./ThreadActivitySteps";
import { conversationLaunchRate, launchConversation, pickConversationTwins, runningConversationLaunch } from "./conversationLaunch";
import type { ConversationLaunchResult } from "./conversationLaunch";
import { takeRoomsReadMark } from "./useConversationLaunchRooms";

export interface ConversationLaunchPick {
  personaTokens: Array<string>;
  reason: string;
}

/**
 * A room this card started. `createdMark` was taken when the create resolved: only a list read
 * begun after it decides whether the room still exists. Earlier reads may land later and not hold it.
 */
export interface ConversationLaunchPendingRoom {
  token: string;
  participantCount?: number;
  createdMark: number;
}

export interface ThreadConversationLaunchCardProps {
  prepared: PreparedConversation;
  threadToken: string;
  /** The saved message this card sits on. Until it is known nothing can be linked to it. */
  sourceMessageToken?: string;
  threadLanguage?: string | null;
  /** The thread's rooms. Null until the list has loaded: Start waits for it. */
  linkedRooms: Array<AssistantLinkedRoom> | null;
  /** When the read behind `linkedRooms` began (`takeRoomsReadMark`). */
  linkedRoomsReadMark: number;
  /** The list could not be read, so Start stays blocked: the card says why. */
  hasRoomsReadFailed?: boolean;
  /** The room created through Review twins, whose window lives on the page. */
  reviewedRoom?: ConversationLaunchPendingRoom;
  onRoomStarted: (roomToken: string, openingQuestion: string) => void;
  onReviewTwins: (picked: ConversationLaunchPick) => void;
  onOpenRoom: (roomToken: string) => void;
}

/*
 * Rooms started from a card, by source message: a launch can finish while its card is unmounted,
 * and the card mounted next may hold a list read before the room existed.
 */
const roomsStartedByMessage = new Map<string, ConversationLaunchPendingRoom>();

/**
 * The Assistant's prepared conversation: nothing exists until Start. Same minimal card as
 * `ThreadImageApprovalGroup`. The rate is read live and only ever comes from the audience, so a
 * rate that cannot be read leaves Review twins alone, whose window quotes its own.
 */
export function ThreadConversationLaunchCard(props: ThreadConversationLaunchCardProps) {
  const { prepared, threadToken, sourceMessageToken, threadLanguage, linkedRooms, linkedRoomsReadMark, hasRoomsReadFailed, reviewedRoom, onRoomStarted, onReviewTwins, onOpenRoom } = props;

  /* The card is thread chrome: same language as the composer and step copy (`getThreadPlaceholders`), not the interface's. */
  const copyLanguage = resolveThreadLanguage(threadLanguage);
  const copy = getThreadPlaceholders(copyLanguage).conversationLaunch;
  const { data: creditInfo } = useCreditInfo();
  /* undefined: still reading. null: could not be read. */
  const [audience, setAudience] = useState<AudienceSummary | null | undefined>(undefined);
  const [busy, setBusy] = useState<"start" | "review" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isTrialCapOpen, setIsTrialCapOpen] = useState(false);
  const [startedRoom, setStartedRoom] = useState<ConversationLaunchPendingRoom | null>(null);
  const isBusyRef = useRef(false);
  /*
   * The page reuses itself across threads, so a launch can answer after this card left: its parent
   * callbacks then act on another thread. Only `roomsStartedByMessage` outlives the card.
   */
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const audienceToken = prepared.twins.audienceToken;
  useEffect(() => {
    let isCancelled = false;
    setAudience(undefined);
    populationsApi.get(audienceToken).then(
      (loaded) => {
        if (!isCancelled) {
          setAudience(loaded ?? null);
        }
      },
      () => {
        if (!isCancelled) {
          setAudience(null);
        }
      },
    );
    return () => {
      isCancelled = true;
    };
  }, [audienceToken]);

  const followLaunch = async (launch: Promise<ConversationLaunchResult>, messageToken: string) => {
    isBusyRef.current = true;
    setBusy("start");
    setError(null);
    try {
      const result = await launch;
      if (result.status === "started") {
        const room = { token: result.roomToken, participantCount: result.participantCount, createdMark: takeRoomsReadMark() };
        roomsStartedByMessage.set(messageToken, room);
        if (isMountedRef.current) {
          setStartedRoom(room);
          onRoomStarted(result.roomToken, prepared.openingQuestion);
        }
      } else if (!isMountedRef.current) {
        return;
      } else if (result.status === "review") {
        onReviewTwins({ personaTokens: result.personaTokens, reason: result.reason });
      } else if (result.reason === "trial_capped") {
        setIsTrialCapOpen(true);
      } else {
        setError(result.message);
      }
    } finally {
      isBusyRef.current = false;
      setBusy(null);
    }
  };

  /* A launch begun before this card mounted: hold both buttons and take its result. */
  useEffect(() => {
    const running = sourceMessageToken ? runningConversationLaunch(sourceMessageToken) : undefined;
    if (running && sourceMessageToken && !isBusyRef.current) {
      void followLaunch(running, sourceMessageToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceMessageToken]);

  const onStart = async () => {
    if (isBusyRef.current || !sourceMessageToken || linkedRooms === null) {
      return;
    }
    await followLaunch(
      launchConversation({
        prepared,
        threadToken,
        sourceMessageToken,
        language: threadLanguage || audience?.language?.trim() || defaultContentLanguageFromUi(),
        copyLanguage,
      }),
      sourceMessageToken,
    );
  };

  const onReview = async () => {
    if (isBusyRef.current || linkedRooms === null) {
      return;
    }
    isBusyRef.current = true;
    setBusy("review");
    setError(null);
    try {
      const result = await pickConversationTwins(prepared, undefined, copyLanguage);
      if (!isMountedRef.current) {
        return;
      }
      if (result.status === "review") {
        onReviewTwins({ personaTokens: result.personaTokens, reason: result.reason });
      } else {
        setError(result.message);
      }
    } finally {
      isBusyRef.current = false;
      setBusy(null);
    }
  };

  const listedRoom = sourceMessageToken
    ? linkedRooms?.find((room) => room.sourceMessageToken === sourceMessageToken)
    : undefined;
  const earlierRoom = sourceMessageToken ? roomsStartedByMessage.get(sourceMessageToken) : undefined;
  const pendingRoom = [startedRoom, earlierRoom, reviewedRoom].find((room) => room && linkedRoomsReadMark < room.createdMark) ?? null;
  const linkedToken = listedRoom?.token ?? pendingRoom?.token ?? null;
  const twinCount = listedRoom?.participantCount ?? pendingRoom?.participantCount ?? prepared.participantCount;

  /* No credit info yet means enrolment is unknown, and either price could be the wrong one. */
  const rate = creditInfo ? conversationLaunchRate(audience, prepared.participantCount, isMeteringEnrolled(creditInfo)) : null;
  const isRateLoading = audience === undefined;
  const canStart = rate !== null && Boolean(sourceMessageToken) && linkedRooms !== null;

  let footer;
  if (linkedToken) {
    footer = (
      <Button
        variant="outline"
        className="w-full rounded-xl sm:w-auto"
        data-testid="conversation-launch-open"
        onClick={() => onOpenRoom(linkedToken)}
      >
        {copy.open}
      </Button>
    );
  } else {
    footer = (
      <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end">
        <Button
          variant="outline"
          className="rounded-xl"
          disabled={busy !== null || !sourceMessageToken || linkedRooms === null}
          data-testid="conversation-launch-review"
          onClick={() => void onReview()}
        >
          {busy === "review" ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden /> : null}
          {copy.review}
        </Button>
        {rate !== null || isRateLoading ? (
          <Button
            className="gap-2 rounded-xl"
            disabled={busy !== null || !canStart}
            data-testid="conversation-launch-start"
            onClick={() => void onStart()}
          >
            {busy === "start" ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden /> : null}
            {copy.start}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-4 rounded-2xl border border-primary/20 bg-background p-4 sm:p-5"
      data-testid="thread-conversation-launch"
    >
      <TrialLimitUpgradeModal open={isTrialCapOpen} onClose={() => setIsTrialCapOpen(false)} kind="rooms" />
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/15 text-primary">
          <MessagesSquare className="h-5 w-5" aria-hidden />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <Text size="sm" className="break-words leading-5" data-testid="conversation-launch-topic">
            {prepared.topic}
          </Text>
          <Text size="xs" color="muted" className="break-words" data-testid="conversation-launch-meta">
            {`${prepared.audienceName} · ${formatActivityPlural(copy.twins, twinCount, copyLanguage)}`}
          </Text>
        </div>
      </div>
      <Text size="sm" className="whitespace-pre-wrap break-words leading-5" data-testid="conversation-launch-question">
        {prepared.openingQuestion}
      </Text>
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:gap-3">
        {!linkedToken && rate !== null ? (
          <Text size="xs" color="muted" data-testid="conversation-launch-rate">
            {formatActivityPlural(copy.rate, rate, copyLanguage)}
          </Text>
        ) : null}
        <div className="flex min-w-0 flex-col items-stretch gap-2 sm:ml-auto sm:items-end">
          {footer}
          {error ? (
            <Text size="xs" color="muted" role="alert" data-testid="conversation-launch-error">
              {error}
            </Text>
          ) : null}
          {!linkedToken && linkedRooms === null && hasRoomsReadFailed ? (
            <Text size="xs" color="muted" role="alert" data-testid="conversation-launch-rooms-unavailable">
              {copy.roomsUnavailable}
            </Text>
          ) : null}
        </div>
      </div>
    </div>
  );
}
